import { useCallback } from "react";
import { useAppStore } from "@/store";
import { EventContext } from "@/domains/metrics/context";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { isEditableElement } from "@/modules/html";
import { useEventListener } from "usehooks-ts";

/**
 * Paste handler that creates a new note when pasting outside editable fields.
 *
 * This hook listens to document-level paste events and:
 * 1. Checks if the paste originated from an editable field (input, textarea, contentEditable)
 * 2. If yes, allows native paste behavior to proceed
 * 3. If no, prevents default and creates a new note with the pasted content
 *
 * The paste event bubbles up the DOM, so we check event.target (where paste originated)
 * rather than document.activeElement.
 *
 * @param options.isEnabled - Whether paste handling is enabled for the current view
 */
export const usePasteHandler = ({ isEnabled = true }: { isEnabled?: boolean } = {}) => {
  const { store } = useAppStore();
  const handlePaste = useCallback(
    (event: ClipboardEvent) => {
      const target = event.target as Element;

      // common editor neatly does this
      if (event.defaultPrevented) {
        return;
      }

      if (!isEnabled) {
        return;
      }

      // If paste originated from an editable field, let native paste proceed
      if (isEditableElement(target)) {
        logger.debug({
          message: "[usePasteHandler] Paste in editable field, allowing native behavior",
          info: {
            tagName: target.tagName,
            className: target.className,
            isContentEditable: (target as HTMLElement).isContentEditable,
          },
        });
        return;
      }

      // Check if a note is currently being created
      if (store.notes.newNoteIsBeingCreated) {
        return;
      }

      // Get clipboard data
      const clipboardData = event.clipboardData;
      if (!clipboardData) {
        logger.debug({
          message: "[usePasteHandler] No clipboard data available",
        });
        return;
      }

      // Check if clipboard has any content
      const clipboardTypes = Array.from(clipboardData.types);
      const hasText = clipboardTypes.includes("text/plain");
      const hasHtml = clipboardTypes.includes("text/html");
      const hasFiles = clipboardData.files.length > 0;

      if (!hasText && !hasHtml) {
        logger.debug({
          message: "[usePasteHandler] Clipboard is empty",
        });
        return;
      }

      if (hasFiles) {
        // we allow Drag and Drop to haddle files
        logger.debug({
          message: "[usePasteHandler] Clipboard has files",
        });

        return;
      }

      // Prevent default paste behavior
      event.preventDefault();
      event.stopPropagation();

      try {
        logger.debug({
          message: "[usePasteHandler] Creating note from paste",
          info: {
            hasText,
            hasHtml,
            hasFiles,
            fileCount: clipboardData.files.length,
          },
        });

        store.notes.composeNewNote({
          eventContext: EventContext.NotesActions,
          clipboardEvent: event,
          autoFocusPosition: "end",
        });
      } catch (error) {
        logger.error({
          message: "[usePasteHandler] Error creating note from paste",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      }
    },
    [isEnabled, store]
  );

  useEventListener("paste", handlePaste);
};
